/**
 * Player titles — what a bought title *is*, as opposed to what it costs.
 *
 * The split is the same one `weapons.ts` makes against the catalogue, and for
 * the same reason. `catalogue.data.ts` holds the price, the tier and the sort
 * order, because every row there becomes a Postgres column; this holds the
 * wording, the colour and the treatment, because those are read by the
 * **browser, mid-battle, with no garage in the process** — a nameplate is drawn
 * from a snapshot and an id, and there is no overview to look a colour up in.
 *
 * Shared, so it must stay browser-safe: no `@nestjs/*`, no Prisma, no Node.
 * There are deliberately no imports at all.
 *
 * ── Why an id crosses the wire and not the finished word ─────────────────────
 * Exactly the argument the map id makes. Both sides hold this table, so the
 * server sends `title_ironback` and the client resolves the word, the colour and
 * the treatment — rather than sending three fields and letting a modified client
 * decide it is called something else. It also means retuning a colour is a
 * deploy rather than a migration.
 *
 * ── The palette has one hard constraint ──────────────────────────────────────
 * **No saturated red and no saturated blue.** Friend and foe are painted on the
 * nameplate itself — an enemy's health bar is always red and their nickname is
 * the identical hex, bold, which is the *only* place a tank's side shows any
 * more (the hull itself carries no team colour) — so a title in either of
 * those colours would be competing with the one signal on that plate a player
 * must never misread. Everything here is a bone, sand, copper, brass,
 * verdigris, ash or gold. The one near-blue is `title_nightjar`, and it is the
 * diamond colour the shop already uses for premium everywhere else
 * (`.price.gem`, the top-tier pips), which is the point: a premium title
 * should be recognisable as one.
 */

/**
 * How a title is drawn. Three treatments, and they are a ladder rather than a
 * taste — the shop sells them in that order.
 *
 * - **`plain`** — the word, in its colour. Nothing else.
 * - **`rule`** — the word between two short hairlines, which reads as a ribbon
 *   at a distance where a glyph would not.
 * - **`chip`** — the word inside a tinted, bordered plate. The heaviest, and the
 *   only one that still reads as *a thing somebody put there* at the size a
 *   nameplate is drawn at 60 m.
 *
 * Deliberately a *shape* ladder rather than a size ladder. Making a bought title
 * bigger than a free one is the obvious version and it is the wrong one: a
 * nameplate sits over a battlefield and the tank has to win, so what is sold
 * here is contrast and framing, never area. Every treatment occupies the same
 * one line at the same font size.
 */
export type TitleStyle = 'plain' | 'rule' | 'chip';

/** Everything the client needs to draw a title it has only the id of. */
export interface TitleProfile {
  id: string;
  /**
   * The word itself, exactly as it is worn.
   *
   * It is also the catalogue row's `name`, and `stat-resolution.test.ts` asserts
   * the two agree — the shop must never advertise a word different from the one
   * that ends up over the tank.
   */
  text: string;
  /** Hex, `#rrggbb`. See the palette constraint at the top of this file. */
  colour: string;
  style: TitleStyle;
  /**
   * A small filled lozenge either side of the word. Premium only.
   *
   * It is **not** an insignia and must not become one. The rank art needs 64–80
   * px to be countable and the title-bar chip is already too small at ~32 px; a
   * nameplate line is about 9 px tall, which is below the size at which any
   * shape with internal detail survives. What does survive at 9 px is a solid
   * blob of colour with a recognisable outline, so that is all this is: a 3 px
   * square on its corner, drawn in CSS rather than as a glyph so it cannot fall
   * back to tofu in a font that lacks it, and no art file to 404.
   */
  mark?: boolean;
  /**
   * A small emblem drawn beside the word as a one-colour mask tinted with
   * `colour`. It replaces the old hairlines (`rule`) and corner diamonds
   * (`mark`), which said "ornament" but never *which* title. Filled in for every
   * entry from its id (`/assets/titles/<id>.png`, 64 px, white on alpha), so a
   * new title cannot forget it. Not drawn on the feed variant.
   */
  icon?: string;
  /**
   * Draw the emblem on both sides of the word, the right one flipped, so the
   * title reads as a heraldic pair rather than a word with a sticker. Filled in
   * from `MIRRORED_TITLES` below.
   */
  mirror?: boolean;
  /**
   * Which way the emblem itself looks. The left-hand copy is flipped when it
   * naturally faces left, so that both copies face the word.
   */
  faces?: 'left' | 'right';
  /**
   * A realistic full-colour badge, background included, drawn instead of the
   * one-colour mask. At nameplate size it is a coloured chip; on a shop card it
   * is the whole picture. Filled in from `ART_TITLES` below.
   */
  art?: string;
  /**
   * A moving treatment. Presentation only — nothing on the wire or in the
   * simulation reads it — and a reduced-motion setting turns every one off.
   *
   * - `'glitch'` pixelates the word at random moments, at least once every five
   *   seconds.
   * - `'snowstorm'` blows snow across the whole badge on a wind that gusts and
   *   lulls rather than running at one speed.
   * - `'eclipse'` draws one large glowing sun after the word, with a moon
   *   crossing it: from a third covered, through totality, to a third covered
   *   on the other side, over one 11-minute battle (`ECLIPSE_TRANSIT_SECONDS`).
   *   The whole badge's light follows the transit, golden-hour orange to dusk
   *   violet at totality and back.
   * - `'neon'` draws the word and emblem as lit tubing: a glow that hums, and
   *   a word that flickers now and then like a tube on its way out.
   * - `'starfall'` sends shooting stars across the upper half of the badge — a
   *   bright head and a trail that fades behind it.
   */
  animated?: 'glitch' | 'snowstorm' | 'eclipse' | 'neon' | 'starfall';
}

/**
 * The reserved id that means *wear nothing*.
 *
 * Titles are the one equippable slot where empty is a real state, and the
 * default one — a tank with no hull cannot be drawn, but a player with no title
 * is every player until they buy one. That is why "no title" is **not** a
 * catalogue row: it needs no price, no ownership, no starter grant and no card,
 * and making it an item would make *not wearing one* something to own.
 *
 * `GarageService.equip` accepts this in the title slot and only in the title
 * slot, and clears the column. Nothing on the wire ever carries it: the server
 * sends a title id or sends nothing.
 */
export const NO_TITLE = 'none';

/**
 * Every title in the game.
 *
 * Curated, and that is a decision rather than a shortcut. A free-text field is
 * a moderation surface, and this game has no moderation; a fixed list is also
 * the only way the wording can carry any character, because a set somebody wrote
 * reads like a set somebody wrote. Nothing here names a role that implies
 * authority over other players — no Admin, no Moderator, no Officer — because a
 * cosmetic that lets one player impersonate the operator is a support ticket
 * with a price tag.
 */
const RAW_TITLE_PROFILES: Record<string, TitleProfile> = {
  // ── Plain: the entry shelf ────────────────────────────────────────────────
  title_ironback: { id: 'title_ironback', text: 'Ironback', colour: '#C7D2DC', style: 'plain' },
  title_ridgehunter: {
    id: 'title_ridgehunter',
    text: 'Ridgehunter',
    colour: '#CDB27C',
    style: 'plain',
  },
  title_slagborn: { id: 'title_slagborn', text: 'Slagborn', colour: '#C98457', style: 'plain' },

  // ── Ruled: the middle shelf ───────────────────────────────────────────────
  title_quartermaster: {
    id: 'title_quartermaster',
    text: 'Quartermaster',
    colour: '#C9A63E',
    style: 'rule',
  },
  title_trackbreaker: {
    id: 'title_trackbreaker',
    text: 'Trackbreaker',
    colour: '#8FBFA6',
    style: 'rule',
  },
  title_ashwalker: { id: 'title_ashwalker', text: 'Ashwalker', colour: '#B7AECB', style: 'rule' },

  // ── Chip: the top of the credit ladder ────────────────────────────────────
  title_deadeye: { id: 'title_deadeye', text: 'Deadeye', colour: '#E8C25A', style: 'chip' },

  // ── Chip and marked: diamonds only ────────────────────────────────────────
  title_warhound: {
    id: 'title_warhound',
    text: 'Warhound',
    // Started as the HUD's own accent, `#E8722C`, which was the obvious choice
    // and the wrong one: it measures 47 units from the hostile health bar's
    // `#D9483B`, closer than any other colour on this shelf by a factor of two.
    // A bought word next to a nickname is not where the enemy cue should be
    // approximated. Pulled toward amber, it is 88 units clear and still the
    // hottest thing on the shelf.
    colour: '#E89A5A',
    style: 'chip',
    mark: true,
  },
  title_nightjar: {
    id: 'title_nightjar',
    // Was `Kestrel`, which is also the call sign of a bot in `battle/bots.ts`.
    // A bought word over one tank that is another tank's *name* two plates along
    // is a collision nobody would ever debug, and it cost nothing to move.
    text: 'Nightjar',
    colour: '#9FD7F5',
    style: 'chip',
    mark: true,
  },
  title_forgemaster: {
    id: 'title_forgemaster',
    text: 'Forgemaster',
    colour: '#F3D98A',
    style: 'chip',
    mark: true,
  },
  // ── Chip and iconed: the 2026-10 diamond additions ────────────────────────
  title_stormcaller: {
    id: 'title_stormcaller',
    text: 'Stormcaller',
    colour: '#C7B8FF',
    style: 'chip',
  },
  title_sandviper: {
    id: 'title_sandviper',
    text: 'Sandviper',
    colour: '#B8D86A',
    style: 'chip',
  },
  title_cindermaw: {
    id: 'title_cindermaw',
    text: 'Cindermaw',
    colour: '#FFB347',
    style: 'chip',
  },
  title_frostbite: {
    id: 'title_frostbite',
    text: 'Frostbite',
    colour: '#B5EDE4',
    style: 'chip',
  },
  title_overlord: {
    id: 'title_overlord',
    text: 'Overlord',
    colour: '#D9A6C8',
    style: 'chip',
  },

  // ── The animated one ──────────────────────────────────────────────────────
  title_glitchborn: {
    id: 'title_glitchborn',
    text: 'Glitchborn',
    colour: '#6BFFB5',
    style: 'chip',
    animated: 'glitch',
  },

  // ── The 2026-10 shelf fill: three for credits, seven for diamonds ─────────
  // Spread across tiers 1–5 so every depth of the shelf has something on it.
  // Credits keep to `rule` until tier 4; every diamond title is a chip.
  title_dustrunner: {
    id: 'title_dustrunner',
    text: 'Dustrunner',
    colour: '#D8C9A3',
    style: 'rule',
  },
  title_rivetjack: {
    id: 'title_rivetjack',
    text: 'Rivetjack',
    colour: '#B9C9A0',
    style: 'rule',
  },
  title_holdfast: {
    id: 'title_holdfast',
    text: 'Holdfast',
    // Neon pink: saturated, but nowhere near the enemy red or the ally teal.
    colour: '#FF6EC7',
    style: 'chip',
    animated: 'neon',
  },
  title_lodestar: { id: 'title_lodestar', text: 'Lodestar', colour: '#FFF0A8', style: 'chip' },
  title_gravedigger: {
    id: 'title_gravedigger',
    text: 'Gravedigger',
    colour: '#C8CCB8',
    style: 'chip',
  },
  title_anvilheart: {
    id: 'title_anvilheart',
    text: 'Anvilheart',
    colour: '#E0B98C',
    style: 'chip',
  },
  title_starfall: {
    id: 'title_starfall',
    text: 'Starfall',
    colour: '#E6D7FF',
    style: 'chip',
    animated: 'starfall',
  },
  title_siegebreaker: {
    id: 'title_siegebreaker',
    text: 'Siegebreaker',
    colour: '#F4C9A8',
    style: 'chip',
  },
  title_summit: {
    id: 'title_summit',
    text: 'Summit',
    colour: '#F5F2E6',
    style: 'chip',
    animated: 'snowstorm',
  },
  title_eclipse: {
    id: 'title_eclipse',
    text: 'Eclipse',
    colour: '#E9C6FF',
    style: 'chip',
    animated: 'eclipse',
  },
};

/** How long the `'eclipse'` moon takes to cross the sun once: one battle. */
export const ECLIPSE_TRANSIT_SECONDS = 11 * 60;

/**
 * How far off the sun's centre the moon starts and ends, as a fraction of the
 * sun's diameter. Two equal discs this far apart overlap by exactly a third of
 * the sun's area — solved numerically, since the lens area has no inverse.
 */
export const ECLIPSE_EDGE_OFFSET = 0.5533;

/** Titles whose emblem is shown on both sides of the word (the right one flipped). */
const MIRRORED_TITLES: ReadonlySet<string> = new Set([
  'title_ironback',
  'title_slagborn',
  'title_warhound',
  'title_nightjar',
  'title_sandviper',
  'title_cindermaw',
  'title_frostbite',
  'title_overlord',
  'title_glitchborn',
  'title_dustrunner',
  'title_holdfast',
  'title_lodestar',
  'title_starfall',
  'title_summit',
]);

/** Titles whose emblem naturally faces left (the snake), so the left copy is flipped. */
const FACES_LEFT: ReadonlySet<string> = new Set(['title_sandviper']);

/** Titles with a realistic full-colour badge in place of the one-colour emblem. */
const ART_TITLES: ReadonlySet<string> = new Set([
  'title_slagborn',
  'title_quartermaster',
  'title_forgemaster',
  'title_overlord',
]);

/** Every profile with its emblem, mirroring and badge art filled in from its id. */
export const TITLE_PROFILES: Readonly<Record<string, TitleProfile>> = Object.fromEntries(
  Object.entries(RAW_TITLE_PROFILES).map(([id, profile]) => [
    id,
    {
      ...profile,
      icon: profile.icon ?? `/assets/titles/${id}.png`,
      mirror: MIRRORED_TITLES.has(id),
      faces: FACES_LEFT.has(id) ? 'left' : 'right',
      art: ART_TITLES.has(id) ? `/assets/titles/art/${id}.webp` : undefined,
    },
  ]),
);

/** Every title id, in the order the shop lists them. */
export const TITLE_IDS: readonly string[] = Object.keys(TITLE_PROFILES);

/**
 * Resolves a title id, and **cannot fail**.
 *
 * Total on purpose, in exactly the way `turretFitFor` is: an unknown id, a
 * cleared slot, an id from a build that has since dropped it, or `NO_TITLE`
 * itself all answer `null`, which every caller draws as *no title*. Never the
 * literal id, never a broken mark. A cosmetic lookup must not be able to put a
 * defect on somebody's nameplate.
 */
export function titleFor(id: string | null | undefined): TitleProfile | null {
  if (!id || id === NO_TITLE) return null;
  return TITLE_PROFILES[id] ?? null;
}

/**
 * A title's colour at a given alpha, as an `rgba()` string.
 *
 * Here rather than in the component because both the arena HUD and the shop card
 * need the same tint for the `chip` treatment, and two hex-to-rgb helpers is two
 * chances for the shop's swatch to disagree with the plate it is selling. Falls
 * back to a neutral rather than throwing, for the same reason `titleFor` is
 * total.
 */
export function titleTint(colour: string, alpha: number): string {
  const hex = colour.replace('#', '');
  if (hex.length !== 6) return `rgba(200, 200, 200, ${alpha})`;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return `rgba(200, 200, 200, ${alpha})`;
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
